import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { categoriesService, organizationsService } from "../services/api";

const OrganizationsListedAddPage = () => {
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const [phoneCheck, setPhoneCheck] = useState({
    checked: false,
    valid: true,
    available: true,
    message: ""
  });
  const [categorySearch, setCategorySearch] = useState("");
  const [allCategories, setAllCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city_id: "",
    district_id: "",
    phone: "",
    latitude: "",
    longitude: "",
    category_ids: []
  });

  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      const response = await categoriesService.getAllFlat();
      setLoadingCategories(false);
      if (!response.success) {
        toast.error(response.error || "Kategoriler alınamadı");
        return;
      }
      setAllCategories(Array.isArray(response.data) ? response.data : []);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadCities = async () => {
      setLoadingCities(true);
      const response = await organizationsService.getCities();
      setLoadingCities(false);
      if (!response.success) {
        toast.error(response.error || "Şehirler alınamadı");
        return;
      }
      setCities(Array.isArray(response.data) ? response.data : []);
    };
    loadCities();
  }, []);

  useEffect(() => {
    const cityId = Number(formData.city_id);
    if (!cityId) {
      setDistricts([]);
      setFormData((prev) => ({ ...prev, district_id: "" }));
      return;
    }
    const loadDistricts = async () => {
      setLoadingDistricts(true);
      const response = await organizationsService.getDistrictsByCity(cityId);
      setLoadingDistricts(false);
      if (!response.success) {
        toast.error(response.error || "İlçeler alınamadı");
        setDistricts([]);
        return;
      }
      setDistricts(Array.isArray(response.data) ? response.data : []);
      setFormData((prev) => ({ ...prev, district_id: "" }));
    };
    loadDistricts();
  }, [formData.city_id]);

  const normalizePhoneDigits = (raw) => String(raw || "").replace(/\D/g, "").slice(-10);

  useEffect(() => {
    const phone = formData.phone.trim();
    const phoneDigits = normalizePhoneDigits(phone);
    setPhoneCheck({
      checked: false,
      valid: true,
      available: true,
      message: ""
    });
    if (!phone) return undefined;
    if (phoneDigits.length < 10) {
      setPhoneCheck({
        checked: true,
        valid: false,
        available: false,
        message: "Telefon kontrolü için en az 10 hane girin"
      });
      return undefined;
    }

    const timer = setTimeout(async () => {
      setPhoneCheckLoading(true);
      const response = await organizationsService.checkPhoneAvailability(phone);
      setPhoneCheckLoading(false);
      if (!response.success) {
        setPhoneCheck({
          checked: true,
          valid: false,
          available: false,
          message: response.error || "Telefon kontrolü yapılamadı"
        });
        return;
      }
      setPhoneCheck({
        checked: true,
        valid: response.valid !== false,
        available: !!response.available,
        message: response.message || ""
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.phone]);

  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return allCategories;
    return allCategories.filter((c) => String(c.name || "").toLowerCase().includes(q));
  }, [allCategories, categorySearch]);

  const selectedCategoryCount = formData.category_ids.length;

  const handleCategoryToggle = (categoryId) => {
    setFormData((prev) => {
      const exists = prev.category_ids.includes(categoryId);
      return {
        ...prev,
        category_ids: exists
          ? prev.category_ids.filter((id) => id !== categoryId)
          : [...prev.category_ids, categoryId]
      };
    });
  };

  const setField = (key, value) => {
    if (formError) setFormError("");
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      city_id: "",
      district_id: "",
      phone: "",
      latitude: "",
      longitude: "",
      category_ids: []
    });
    setFormError("");
    setCategorySearch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!formData.name.trim() || !formData.city_id || !formData.district_id || !formData.phone.trim()) {
      toast.error("Lütfen zorunlu alanları doldurun");
      return;
    }
    if (!phoneCheck.checked || !phoneCheck.valid || !phoneCheck.available) {
      const msg = phoneCheck.message || "Telefon numarası uygun değil. Lütfen farklı bir numara girin";
      setFormError(msg);
      toast.error(msg);
      return;
    }
    if (formData.category_ids.length === 0) {
      toast.error("En az bir kategori seçmelisiniz");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      city: cities.find((c) => Number(c.id) === Number(formData.city_id))?.name || "",
      district: districts.find((d) => Number(d.id) === Number(formData.district_id))?.name || "",
      phone: formData.phone.trim(),
      category_ids: formData.category_ids,
      latitude: formData.latitude.trim() || null,
      longitude: formData.longitude.trim() || null
    };

    setSaving(true);
    const response = await organizationsService.createListedOrganization(payload);
    setSaving(false);

    if (!response.success) {
      const msg = response.error || response.message || "Organizasyon eklenemedi";
      setFormError(msg);
      toast.error(msg);
      return;
    }

    toast.success("Organizasyon listelemeye eklendi ve vitrinde yayınlandı");
    resetForm();
  };

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title="Listelemeye Organizasyon Ekle" />

      <div className="card mb-24">
        <div className="card-body p-24">
          <h5 className="mb-2">Tek Tek Organizasyon Ekle</h5>
          <p className="text-secondary-light mb-0">
            Bu formdan eklenen organizasyonlar otomatik olarak vitrine yayınlanır
            <span className="fw-semibold"> (randu_listed=1)</span>.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Organizasyon Adı <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="Örn: Demo Kuaför"
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Şehir <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={formData.city_id}
                  onChange={(e) => setField("city_id", e.target.value)}
                  required
                >
                  <option value="">{loadingCities ? "Şehirler yükleniyor..." : "Şehir seçin"}</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">İlçe <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={formData.district_id}
                  onChange={(e) => setField("district_id", e.target.value)}
                  disabled={!formData.city_id || loadingDistricts}
                  required
                >
                  <option value="">
                    {!formData.city_id
                      ? "Önce şehir seçin"
                      : loadingDistricts
                        ? "İlçeler yükleniyor..."
                        : "İlçe seçin"}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>{district.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Adres</label>
                <input
                  className="form-control"
                  value={formData.address}
                  onChange={(e) => setField("address", e.target.value)}
                  placeholder="Örn: Örnek Mah. Örnek Sok. No:12"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Telefon <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  value={formData.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="Örn: +90 555 123 45 67"
                  required
                />
                <div className="mt-1" style={{ fontSize: 12 }}>
                  {phoneCheckLoading ? (
                    <span className="text-secondary-light">Telefon kontrol ediliyor...</span>
                  ) : phoneCheck.checked ? (
                    phoneCheck.available && phoneCheck.valid ? (
                      <span className="text-success">{phoneCheck.message || "Telefon uygun"}</span>
                    ) : (
                      <span className="text-danger">{phoneCheck.message || "Telefon kullanılamaz"}</span>
                    )
                  ) : (
                    <span className="text-secondary-light">Numara girince otomatik kontrol edilir</span>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label">Enlem</label>
                <input
                  className="form-control"
                  value={formData.latitude}
                  onChange={(e) => setField("latitude", e.target.value)}
                  placeholder="Örn: 40.9950"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Boylam</label>
                <input
                  className="form-control"
                  value={formData.longitude}
                  onChange={(e) => setField("longitude", e.target.value)}
                  placeholder="Örn: 29.0275"
                />
              </div>

              <div className="col-12">
                <label className="form-label">Kategori <span className="text-danger">*</span></label>
                <div className="border rounded p-3">
                  <input
                    className="form-control mb-3"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="Kategori ara..."
                  />

                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <small className="text-secondary-light">
                      Seçili kategori: <b>{selectedCategoryCount}</b>
                    </small>
                    {selectedCategoryCount > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setField("category_ids", [])}
                      >
                        Temizle
                      </button>
                    )}
                  </div>

                  <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                    {loadingCategories ? (
                      <span className="text-secondary-light">Kategoriler yükleniyor...</span>
                    ) : filteredCategories.length === 0 ? (
                      <span className="text-secondary-light">Aramaya uygun kategori bulunamadı</span>
                    ) : (
                      filteredCategories.map((cat) => {
                        const checked = formData.category_ids.includes(cat.id);
                        return (
                          <label key={cat.id} className={`badge ${checked ? "bg-primary text-white" : "bg-light text-dark"} p-2`} style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleCategoryToggle(cat.id)}
                              style={{ marginRight: 6 }}
                            />
                            {cat.name}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {formError && (
              <div className="alert alert-danger mt-3 mb-0">
                <b>Kayıt hatası:</b> {formError}
              </div>
            )}

            <div className="d-flex align-items-center gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                disabled={saving || phoneCheckLoading || (phoneCheck.checked && (!phoneCheck.valid || !phoneCheck.available))}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <Icon icon="solar:add-circle-outline" />}
                {saving ? "Ekleniyor..." : "Ekle"}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
                Formu Temizle
              </button>
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
};

export default OrganizationsListedAddPage;
